import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { card, cardHead, muted } from './Overview';

// "Definicje i model" (spec 7.12): documentation inside the product — tables, formulas and the
// thresholds the state engine actually runs with, so nobody computes Availability their own way.

interface Defs { min_speed_m_min: number; hysteresis_seconds: number; micro_stop_seconds: number; short_stop_seconds: number; stale_seconds: number; diameter_tolerance_mm: number; shift_hours: [number, number]; timezone: string; poll_interval_seconds: number }

const Code: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <pre style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 14px', fontSize: 12.5, lineHeight: 1.55, overflowX: 'auto', margin: '6px 0 12px', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>{children}</pre>
);

const Definitions: React.FC = () => {
  const { t } = useLanguage();
  const [d, setD] = useState<Defs | null>(null);
  useEffect(() => { api.get('/analytics/definitions').then(r => setD(r.data)).catch(() => null); }, []);
  const v = (k: keyof Defs) => (d ? String(d[k]) : '…');

  return (
    <div style={{ fontFamily: 'Inter, system-ui, sans-serif', maxWidth: 1100 }}>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#1e293b' }}>📖 {t('definitions.title')}</h1>
        <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: 14 }}>{t('definitions.subtitle')}</p>
      </div>

      <div style={card}>
        <div style={cardHead}><b>{t('definitions.sources')}</b></div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <tbody>
            {[
              ['line_telemetry_readings', t('definitions.srcTelemetry', { sec: v('poll_interval_seconds') })],
              ['line_state_events', t('definitions.srcStates')],
              ['winder_cycles', t('definitions.srcWinder')],
              ['production_history', t('definitions.srcHistory')],
              ['production_assignment_items', t('definitions.srcOrders')],
              ['materials', t('definitions.srcMaterials')],
              ['production_standards', t('definitions.srcStandards')],
              ['production_line_logins', t('definitions.srcLogins')],
            ].map(([k, desc]) => (
              <tr key={k}><td style={{ padding: '6px 10px 6px 0', fontFamily: 'ui-monospace, monospace', fontWeight: 700, whiteSpace: 'nowrap', verticalAlign: 'top', borderBottom: '1px solid #f1f5f9' }}>{k}</td><td style={{ padding: '6px 0', color: '#334155', borderBottom: '1px solid #f1f5f9' }}>{desc}</td></tr>
            ))}
          </tbody>
        </table>
        <p style={{ ...muted, marginTop: 10 }}>{t('definitions.principle')}</p>
      </div>

      <div style={card}>
        <div style={cardHead}><b>{t('definitions.engine')}</b><span style={muted}>{t('definitions.engineSub')}</span></div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead><tr style={{ background: '#f8f9fa' }}><th style={thL}>{t('definitions.colState')}</th><th style={thL}>{t('definitions.colCondition')}</th></tr></thead>
          <tbody>
            <tr><td style={tdL}><b>prod</b></td><td style={tdL}>{t('definitions.stateProd', { min: v('min_speed_m_min') })}</td></tr>
            <tr><td style={tdL}><b>down</b></td><td style={tdL}>{t('definitions.stateDown')}</td></tr>
            <tr><td style={tdL}><b>nodata</b></td><td style={tdL}>{t('definitions.stateNoData', { sec: v('stale_seconds') })}</td></tr>
          </tbody>
        </table>
        <ul style={{ fontSize: 13, color: '#334155', lineHeight: 1.6, paddingLeft: 20, marginTop: 12 }}>
          <li>{t('definitions.hysteresis', { sec: v('hysteresis_seconds') })}</li>
          <li>{t('definitions.microStop', { sec: v('micro_stop_seconds') })}</li>
          <li>{t('definitions.shortStop', { min: d ? String(Math.round(d.short_stop_seconds / 60)) : '…' })}</li>
          <li>{t('definitions.changeover')}</li>
          <li>{t('definitions.shifts', { s1: v('shift_hours').split(',')[0] ?? '7', s2: v('shift_hours').split(',')[1] ?? '19', tz: v('timezone') })}</li>
          <li>{t('definitions.thresholdsNote')}</li>
        </ul>
      </div>

      <div style={card}>
        <div style={cardHead}><b>{t('definitions.formulas')}</b></div>
        <b style={{ fontSize: 13 }}>{t('definitions.fTime')}</b>
        <Code>{`czas_dostępny = prod + krótkie_zatrzymania + pozostałe_przestoje     (nodata POZA mianownikiem)
Availability  = prod / czas_dostępny`}</Code>
        <b style={{ fontSize: 13 }}>{t('definitions.fSpeed')}</b>
        <Code>{`średnia_prędkość = Σ(avg_speed × czas odcinka prod) / Σ czas odcinków prod     (nigdy z zerami z postojów)
Performance      = średnia_prędkość / target(linia, materiał, data)             (tylko czas z targetem)`}</Code>
        <b style={{ fontSize: 13 }}>{t('definitions.fMass')}</b>
        <Code>{`g/m           = π · (Ø/2)² · ρ · 100 / 1000        Ø [mm] z pozycji, ρ [g/cm³] z Materiałów
kg_nawijarka  = Σ metry cyklu × g/m / 1000             kg z przepływu (linie z nawijarką)
kg_netto      = Σ szt. × masa nominalna szpuli          kg rozliczeniowe (wszystkie linie)
kg/h          = kg_netto / (prod w godzinach)           na godzinę PRODUKCJI, nie zegarową`}</Code>
        <b style={{ fontSize: 13 }}>{t('definitions.fCapacity')}</b>
        <Code>{`target_kg/h   = target × 60 × g/m / 1000
potencjał     = Σ (czas_dostępny odcinka / 3600) × target_kg/h     tylko odcinki z targetem i gęstością
strata_przestoje = czas przestojów × target_kg/h
strata_prędkość  = czas prod × target_kg/h − kg_brutto
strata_scrap     = potwierdzone niepełne szpule
potencjał − przestoje − krótkie − prędkość − scrap = kg_netto          ← musi się domykać`}</Code>
        <b style={{ fontSize: 13 }}>{t('definitions.fQuality')}</b>
        <Code>{`owalność            = |Ø_X − Ø_Y|
% poza tolerancją   = udział odczytów prod z |Ø − nominał| > ${v('diameter_tolerance_mm')} mm
best demonstrated   = 95. percentyl avg_speed odcinków prod ≥ 20 min z ostatnich 90 dni`}</Code>
      </div>

      <div style={card}>
        <div style={cardHead}><b>{t('definitions.decisions')}</b></div>
        <ol style={{ fontSize: 13, color: '#334155', lineHeight: 1.7, paddingLeft: 20, margin: 0 }}>
          <li>{t('definitions.dec1')}</li>
          <li>{t('definitions.dec2')}</li>
          <li>{t('definitions.dec3')}</li>
          <li>{t('definitions.dec4')}</li>
        </ol>
      </div>
    </div>
  );
};

const thL: React.CSSProperties = { padding: '8px 10px', textAlign: 'left', fontSize: 11, color: '#6c757d', borderBottom: '2px solid #dee2e6' };
const tdL: React.CSSProperties = { padding: '8px 10px', borderBottom: '1px solid #f0f0f0', color: '#334155' };

export default Definitions;
